import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import { useToast } from '@pelckmans/elementis/components/toast';
import DuplicateIcon from '@pelckmans/elementis/icons/duplicate';
import FileIcon from '@pelckmans/elementis/icons/file';
import LinkOnIcon from '@pelckmans/elementis/icons/link-on';
import MaterialPresentationIcon from '@pelckmans/elementis/icons/material-presentation';
import MaterialVideoIcon from '@pelckmans/elementis/icons/material-video';
import { t } from 'i18next';
import { useContext } from 'react';
import { UserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import { TOAST_ID } from '../../../../../constants/ToastIds';
import ModalContext from '../../../../../contexts/ModalContext';
import AddFileMaterialModal from '../../../../../shared-components/elementis/components/add-material-modal/add-file-material-modal';
import AddLinkMaterialModal from '../../../../../shared-components/elementis/components/add-material-modal/add-link-material-modal';
import ShareModal from '../../../../../shared-components/share-modal/ShareModal';
import { TOKEN_ACCESSOR } from '../../../../../utils/tokenAccessor';
import ModuleContext from '../../../_contexts/ModuleContext';
import useSlideSets from '../../../hooks/useSlideSets';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import AddBookwidgetDropdownItem from './add-material-menu-items/AddBookwidgetDropdownItem';
import CopyMaterialModal from './copy-material-modal';
import { useWithLicenseCheck } from './useWithLicenseCheck';

const BASE = 'PAGES.TOC_LEVEL.SECTIONS.TEACHER.MATERIAL';
const ACTION_ADD_LINK_MATERIAL = 1;
const ACTION_ADD_YOUTUBE_MATERIAL = 2;
const ACTION_ADD_FILE_MATERIAL = 3;
const ACTION_COPY_MATERIAL = 5;

export default function AddMaterialButton() {
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { fetchUserMaterial } = useContext(UserMaterialContext);
  const { addToast } = useToast();

  const { buildNewSlideSetUrl } = useSlideSets();
  const { hasLicense, licenseCheck } = useWithLicenseCheck();
  const { showModal } = useContext(ModalContext);

  function onSubmit(userMaterial?: UserMaterial | undefined) {
    addToast({
      id: TOAST_ID.ADD_MATERIAL_SUCCESS,
      variant: 'success',
      title: t('PAGES.TOC_LEVEL.TOASTS.ADD_MATERIAL.SUCCESS.TITLE'),
      children: t('PAGES.TOC_LEVEL.TOASTS.ADD_MATERIAL.SUCCESS.MESSAGE'),
    });

    if (!selectedModule || !selectedNode) return;

    if (userMaterial) {
      showModal(
        <ShareModal
          id="share-modal"
          moduleId={selectedModule.id}
          nodeId={selectedNode.id}
          userMaterials={[userMaterial]}
          onSubmitted={fetchUserMaterial}
        />,
      );
    }

    return fetchUserMaterial();
  }

  function handleOnClick(actionId: number) {
    if (!selectedModule || !selectedNode) return;

    let modal: JSX.Element | undefined;
    switch (actionId) {
      case ACTION_ADD_LINK_MATERIAL:
      case ACTION_ADD_YOUTUBE_MATERIAL:
        modal = (
          <AddLinkMaterialModal
            id="add-url-material-modal"
            tokenAccessor={TOKEN_ACCESSOR}
            moduleId={selectedModule.id}
            nodeId={selectedNode.id}
            studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
            onSubmit={onSubmit}
            isYouTube={actionId === ACTION_ADD_YOUTUBE_MATERIAL}
          />
        );
        break;
      case ACTION_ADD_FILE_MATERIAL:
        modal = (
          <AddFileMaterialModal
            id="add-file-material-modal"
            tokenAccessor={TOKEN_ACCESSOR}
            moduleId={selectedModule.id}
            nodeId={selectedNode.id}
            studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
            onSubmit={onSubmit}
          />
        );
        break;
      case ACTION_COPY_MATERIAL:
        modal = <CopyMaterialModal id="copy-material-modal" onSubmit={onSubmit} />;
        break;
    }

    if (!modal) return;

    return licenseCheck(() => showModal(modal));
  }

  if (!selectedModule || !selectedNode) return null;

  return (
    <ButtonDropdown
      buttonProps={{ prominence: 'secondary' }}
      title={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.TITLE`)}
      disableBackgroundScroll
    >
      <ButtonDropdown.Item
        key="add-url"
        onClick={handleOnClick(ACTION_ADD_LINK_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_URL`)}
        textIcon={<LinkOnIcon />}
      />
      <ButtonDropdown.Item
        key="add-youtube"
        onClick={handleOnClick(ACTION_ADD_YOUTUBE_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_YOUTUBE`)}
        textIcon={<MaterialVideoIcon />}
      />
      <ButtonDropdown.Item
        key="add-file"
        onClick={handleOnClick(ACTION_ADD_FILE_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_FILE`)}
        textIcon={<FileIcon />}
      />
      <AddBookwidgetDropdownItem
        key="add-bookwidget"
        selectedModule={selectedModule}
        selectedNode={selectedNode}
        onSubmit={onSubmit}
      />

      {selectedModule?.enableSlideSetsAccess && selectedNode && (
        <ButtonDropdown.Item
          key="add-presentation"
          as="link"
          href={
            hasLicense
              ? buildNewSlideSetUrl(selectedModule.id, selectedNode.id, ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL)
              : ''
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={licenseCheck()}
          text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_PRESENTATION`)}
          textIcon={<MaterialPresentationIcon />}
        />
      )}
      <ButtonDropdown.Item
        key="copy-material"
        onClick={handleOnClick(ACTION_COPY_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.COPY_MATERIAL`)}
        textIcon={<DuplicateIcon />}
        showSeparator
      />
    </ButtonDropdown>
  );
}
